import { envConfig } from '#constants/envConfig';

/**
 * Мост к нативному VoiceForegroundPlugin (android/.../VoiceForegroundService.java): табличка «ты в
 * голосе» в шторке и вместе с ней право приложения записывать звук, пока оно свёрнуто.
 *
 * Все вызовы молча ничего не делают в вебе и в старой нативной оболочке без плагина: голос от
 * этого не ломается, он просто остаётся без защиты от глушения в фоне.
 */

export type VoiceForegroundContent = {
  title: string;
  body: string;
  /**
   * Текст для того, кто только слушает. Нативная часть сама смотрит разрешение на микрофон и
   * берёт этот текст, когда разрешения нет: WebView о состоянии разрешения судит ненадёжно -
   * на Android 12 он отвечает «granted» и про отозванное.
   */
  bodyListenOnly?: string;
  leaveLabel: string;
  /**
   * С кем разговор: имя собеседника или сессии. С ним табличка рисуется системным видом «идёт
   * звонок» – с таймером, кнопкой микрофона и красной «положить трубку», и видна на заблокированном
   * экране (02.10.2026). Старая оболочка поле не знает и рисует прежнюю табличку.
   */
  name?: string;
  /** Начало разговора, мс от эпохи: от него идёт таймер. Пока не ответили – не передаётся. */
  startedAt?: number;
  /** Микрофон сейчас включён: от этого зависит значок и подпись кнопки. */
  micOn?: boolean;
  muteLabel?: string;
  unmuteLabel?: string;
};

type PluginListenerHandle = { remove: () => Promise<void> };

interface VoiceForegroundPlugin {
  /** `listenOnly` – сервис поднялся без разрешения на микрофон и защищает только звук. */
  start(options: VoiceForegroundContent): Promise<{ listenOnly?: boolean } | undefined>;
  /** Перерисовать показанную табличку без запуска сервиса. В старой оболочке метода нет. */
  update(options: VoiceForegroundContent): Promise<{ updated?: boolean } | undefined>;
  stop(): Promise<void>;
  addListener(event: 'leave' | 'toggleMic', listener: () => void): Promise<PluginListenerHandle>;
}

let cached: VoiceForegroundPlugin | null | undefined;
let loading: Promise<void> | undefined;

/**
 * Прокси `registerPlugin` – ловушка-thenable: его нельзя ни возвращать из async-функции, ни
 * ждать через await (см. `ensurePlugin` в features/notifications/utils/deviceSettings.ts).
 * Поэтому он только кладётся в переменную модуля и зовётся по методам.
 */
function ensurePlugin(): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    cached = null;
    return Promise.resolve();
  }
  if (cached !== undefined) {
    return Promise.resolve();
  }
  if (!loading) {
    loading = (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        cached = registerPlugin<VoiceForegroundPlugin>('VoiceForeground');
      } catch {
        cached = null;
      }
    })();
  }
  return loading;
}

/**
 * Поднять или обновить табличку. `false` – защита не та, что нужна: нативная часть отказала
 * (приложение уже в фоне) или сервис поднялся «только слушать», а человеку нужен микрофон
 * (`needsMic`) – системный вопрос про микрофон ещё на экране. Тогда вызывающий повторяет, и тот
 * же сервис переключается на микрофон, как только разрешение появилось. Голос от отказа не рвётся.
 * В вебе и в оболочке без плагина – `true`: защищать там нечего, повторять незачем.
 */
export async function startVoiceForeground(
  content: VoiceForegroundContent,
  needsMic = true
): Promise<boolean> {
  await ensurePlugin();
  if (!cached) {
    return true;
  }
  try {
    const result = await cached.start(content);
    return !(needsMic && result?.listenOnly);
  } catch {
    return false;
  }
}

/**
 * Перерисовать уже показанную табличку – можно и из фона, где запуск сервиса Android 12+ не
 * разрешает. Нужна ровно для этого: микрофон выключили кнопкой в шторке, а она осталась прежней до
 * возврата в приложение (телефон, 02.10.2026). Таблички нет или оболочка старая – молча ничего.
 */
export async function updateVoiceForeground(content: VoiceForegroundContent): Promise<void> {
  await ensurePlugin();
  if (!cached) {
    return;
  }
  try {
    await cached.update(content);
  } catch {
    // Старая оболочка без метода: табличка обновится при возврате в приложение.
  }
}

export async function stopVoiceForeground(): Promise<void> {
  await ensurePlugin();
  if (!cached) {
    return;
  }
  try {
    await cached.stop();
  } catch {
    // Старая оболочка без плагина.
  }
}

/** Подписка на «Выйти» из шторки. Возвращает отписку; в вебе – пустую. */
export async function onVoiceForegroundLeave(listener: () => void): Promise<() => void> {
  return listen('leave', listener);
}

/** Подписка на кнопку микрофона в шторке. Возвращает отписку; в вебе – пустую. */
export async function onVoiceForegroundToggleMic(listener: () => void): Promise<() => void> {
  return listen('toggleMic', listener);
}

async function listen(event: 'leave' | 'toggleMic', listener: () => void): Promise<() => void> {
  await ensurePlugin();
  if (!cached) {
    return () => undefined;
  }
  try {
    const handle = await cached.addListener(event, listener);

    return () => {
      void handle.remove();
    };
  } catch {
    return () => undefined;
  }
}
